import { useState } from 'react';
import toast from '@/components/atoms/toaster/helpers/toast';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import Papa from 'papaparse';
import { MAX_CART_ITEMS } from '@/providers/cart/types';
import { CsvParser } from '@/utils/parser';

const useCSVFileHandler = () => {
  const { translate } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<Record<string, string>>({});
  const [readFiles, setReadFiles] = useState<Record<string, string>>({});
  const [loadingProgress, setLoadingProgress] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const handleFile = (inputFiles: File[]) => {
    const newFileError: Record<string, string> = { ...fileError };
    const resultFiles: File[] = [];

    inputFiles.forEach((inputFile) => {
      const alreadyExists = files.find(
        (file) => file.name === inputFile.name && file.type === inputFile.type && file.size === inputFile.size,
      );

      if (alreadyExists) {
        toast.error(
          translate('quick-order.error.file.exists', {
            values: { file: inputFile.name },
          }),
        );
      } else {
        if (inputFile.type != 'text/csv') newFileError[inputFile.name] = translate('quick-order.error.format.file');

        resultFiles.push(inputFile);
      }
    });
    setFileError(newFileError);
    setFiles((array) => [...resultFiles, ...array]);
  };

  const handleFileReader = (files: File[]) => {
    files.forEach((file) => {
      if (!fileError[file.name]) {
        const reader = new FileReader();

        reader.onload = () => {
          const content = reader.result as string;
          const delimiter = CsvParser.guessDelimiter(content);

          const parsed = Papa.parse<string[]>(content, {
            header: false,
            skipEmptyLines: true,
            delimiter,
            quoteChar: '"',
            escapeChar: '"',
          });

          const rows = parsed.data as string[][];
          const headers = rows[0].map((h) => h.toLowerCase());
          const dataRows = rows.slice(1);

          if (rows.length === 0) {
            setFileError((fileError) => ({
              ...fileError,
              [file.name]: translate('quick-order.error.format'),
            }));
            setLoading((loading) => ({ ...loading, [file.name]: false }));
          }

          const expectedColumnCount = headers.length;

          const invalidRows = dataRows.filter((row) => row.length !== expectedColumnCount);
          if (invalidRows.length > 0) {
            setFileError((fileError) => ({
              ...fileError,
              [file.name]: translate('quick-order.error.format'),
            }));
            setLoading((loading) => ({ ...loading, [file.name]: false }));
          }

          const rowsWithPositiveQuantity = dataRows.filter((row) => {
            const [, , , , , , , , , quantity] = row;
            const qty = parseInt(quantity, 10);
            return !isNaN(qty) && qty > 0;
          });

          if (rowsWithPositiveQuantity.length > MAX_CART_ITEMS) {
            setFileError((fileError) => ({
              ...fileError,
              [file.name]: translate('quick-order.error.file.exceeds'),
            }));
            setLoading((loading) => ({ ...loading, [file.name]: false }));
          }

          setReadFiles((readFiles) => ({ ...readFiles, [file.name]: reader.result as string }));
          const includesSKU = headers.includes('sku');
          const includesQuantity = headers.includes('quantity');

          if (!includesSKU || !includesQuantity)
            setFileError((fileError) => ({ ...fileError, [file.name]: translate('quick-order.error.format') }));

          setLoading((loading) => ({ ...loading, [file.name]: false }));
        };
        reader.onprogress = (e) => {
          setLoading((loading) => ({ ...loading, [file.name]: true }));
          setLoadingProgress((loadingProgress) => ({ ...loadingProgress, [file.name]: (e.loaded / e.total) * 100 }));
        };
        reader.onerror = () => {
          setFileError((fileError) => ({ ...fileError, [file.name]: translate('quick-order.error.upload.fail') }));
        };
        reader.readAsText(file);
      }
    });
  };

  const handleClearFiles = () => {
    setFiles([]);
  };

  const handleClearReadFiles = () => {
    setReadFiles({});
  };

  const handleRemoveClick = (removedFile: File) => {
    if (Object.keys(fileError).length > 0) {
      const newFileError = { ...fileError };
      delete newFileError[removedFile.name];
      setFileError(newFileError);
    }
    if (Object.keys(readFiles).length > 0) {
      const newReadFiles = { ...readFiles };
      delete newReadFiles[removedFile.name];
      setReadFiles(newReadFiles);
    }
    setFiles((files) => files.filter((file) => file.name != removedFile.name));
  };

  return {
    files,
    readFiles,
    fileError,
    loadingProgress,
    loading,
    handleFile,
    handleFileReader,
    handleClearFiles,
    handleRemoveClick,
    handleClearReadFiles,
  };
};
export default useCSVFileHandler;
